/**
 * Created by Jerker Nord on 2023-01-19.
 */
import {LonerSymbol} from "./LonerSymbol";
import {LonerSymbolStates} from "../../../../core/reel/ReelInterfaces";
import {gsap} from "gsap";
import {Loner} from "../../../Loner";
import {
    LonerGameAssets,
    SCATTER_TRANSFORM_TEXTURES,
    TV_ANIMATION_NAMES,
    TV_INFECTIOUS_ANIMATION_NAMES,
    TV_SKIN_NAMES
} from "../../../LonerGameAssets";
import {SpineUtils} from "../../../utils/SpineUtils";
import {LonerStatics} from "../../../LonerStatics";
import {LonerWinLineContainer} from "../../../components/LonerWinLineContainer";
import {LonerSound} from "../../../sound/LonerSound";
import {LonerGameMode} from "../../../server/LonerGameMode";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {LonerSpine} from "../../../components/LonerSpine";

export enum SCATTER_ANIMATION_TRACKS {
    tvLand = 0,
    tvTransformed = 1,
    infectious = 2,
    xSplit = 3,
    idleLoop = 4,
    maxWin = 5
}


export class LonerScatterSymbol extends LonerSymbol {

    private _scatterTVSpine:LonerSpine;
    private _scatterTVInfectSpine:LonerSpine;
    private _tvLandTL:gsap.core.Timeline;
    private _tvRevealTL:gsap.core.Timeline;
    private _splitTL:gsap.core.Timeline;
    private _infectiousTL:gsap.core.Timeline;
    private _tvLanded:boolean = false;
    private _tvTransformed:boolean = false;
    private _tvInfectedOrSplit:boolean = false;

    constructor() {
        super();
        this.initAnimation();
    }

    public init(initState:LonerSymbolStates):LonerSymbol {
        if(initState == LonerSymbolStates.NORMAL) {
            initState = LonerSymbolStates.NORMAL_FEATURE;
        }

        return super.init(initState);
    }

    private initAnimation():void {
        this._scatterTVSpine = new LonerSpine(Loner.assetLoader.getSpineAsset(LonerGameAssets.SCATTER_TV_SPINE));
        this._scatterTVInfectSpine = new LonerSpine(Loner.assetLoader.getSpineAsset(LonerGameAssets.SCATTER_TV_INFECT_SPINE));
        this._spineContainer.addChild(this._scatterTVSpine);
        this._scatterTVSpine.visible = false;
        this._spineContainer.addChild(this._scatterTVInfectSpine);
        this._scatterTVInfectSpine.visible = false;
        this._spineContainer.addChild(this._darkOverlay);
        Loner.symbolTopView.addChild(this._spineContainer);
    }

    public getNewStateAnimation(state:LonerSymbolStates, changeSpeed:number = 0, delay:number = 0, newStateAlpha:number = 1):gsap.core.Timeline {
        //Hide spine to show blurred sym on spin out
        if(state == LonerSymbolStates.SPIN_OUT) {
            this._scatterTVSpine.visible = false;
            this._scatterTVInfectSpine.visible = false;
            if(this._splitTL && this._splitTL.isActive()) {
                this._splitTL.totalProgress(1);
            }
            if(this._infectiousTL && this._infectiousTL.isActive()) {
                this._infectiousTL.totalProgress(1);
            }
            if(this.isInfected == true) {
                this.state = LonerSymbolStates.SCATTER_INFECTED;
            }
        }
        return super.getNewStateAnimation(state, changeSpeed, delay, newStateAlpha);
    }

    public getInitialWinAnimation(winContainer:LonerWinLineContainer):gsap.core.Timeline {
        if(this.symbolName == "S") {
            if(this.isInfected == true) {
                let symbolName:string = this.featureTextureName.slice(0, 2);
                let splitLevelString:string = "";
                this._splitLevel = Loner.model.symbolSizes[this.reelId][this.reelPosition - 3];
                if(this._splitLevel > 1) {
                    splitLevelString = "_Split_" + this._splitLevel;
                }
                this.featureTextureName = symbolName + "/" + symbolName + splitLevelString + "_infectious";
                this._winStateNormalSprite.texture = Loner.assetLoader.getTextures(this.featureTextureName + "_win")[0];
                this._winStateAddSprite.texture = Loner.assetLoader.getTextures(this.featureTextureName + "_win_add")[0];
            } else {
                this._winStateNormalSprite.texture = Loner.assetLoader.getTextures(this.symbolName + "/" + this.featureTextureName + "_win")[0];
                this._winStateAddSprite.texture = Loner.assetLoader.getTextures(this.symbolName + "/" + this.featureTextureName + "_win_add")[0];
            }
        }

        return super.getInitialWinAnimation(winContainer);
    }

    public setSpinePosition():void {
        this._spineContainer.addChild(this._darkOverlay);
        this._spineContainer.position.x = LonerStatics.reelPositions[this.reelId].x;
        this._spineContainer.position.y = (LonerStatics.symbolHeight * (this.reelPosition - 1));
        this._spineContainer.visible = true;
    }

    public getSplitTL():gsap.core.Timeline {
        this._tvInfectedOrSplit = true;
        if(this._tvRevealTL && this._tvRevealTL.isActive()) {
            this._tvRevealTL.totalProgress(1);
        }
        if(this._tvLandTL && this._tvLandTL.isActive()) {
            this._tvLandTL.totalProgress(1);
        }
        this._splitTL = gsap.timeline();
        this._splitTL.add(() => {
            this.setSpinePosition();
        });
        this._splitTL.add(() => {
            Loner.soundPlayer.playSound(LonerSound.TV_SPLIT);
            this._scatterTVSpine.visible = true;
            SpineUtils.resetSpineAnimation(this._scatterTVSpine);
            this._scatterTVSpine.state.setEmptyAnimation(SCATTER_ANIMATION_TRACKS.idleLoop, 0);
            this._scatterTVSpine.state.setEmptyAnimation(SCATTER_ANIMATION_TRACKS.tvTransformed, 0);
            this._scatterTVSpine.state.setEmptyAnimation(SCATTER_ANIMATION_TRACKS.tvLand, 0);
        });
        if(this._reelId == 0) {
            this._splitTL.add(() => {
                this._scatterTVSpine.state.setAnimation(SCATTER_ANIMATION_TRACKS.xSplit, TV_ANIMATION_NAMES.x_split_left, false)
            });
            this._splitTL.add(gsap.timeline({delay : this._scatterTVSpine.skeleton.data.findAnimation(TV_ANIMATION_NAMES.x_split_left).duration}));
        } else if(this._reelId == 2) {
            this._splitTL.add(() => {
                this._scatterTVSpine.state.setAnimation(SCATTER_ANIMATION_TRACKS.xSplit, TV_ANIMATION_NAMES.x_split_middle, false)
            });
            this._splitTL.add(gsap.timeline({delay : this._scatterTVSpine.skeleton.data.findAnimation(TV_ANIMATION_NAMES.x_split_middle).duration}));
        } else {
            this._splitTL.add(() => {
                this._scatterTVSpine.state.setAnimation(SCATTER_ANIMATION_TRACKS.xSplit, TV_ANIMATION_NAMES.x_split_right, false)
            });
            this._splitTL.add(gsap.timeline({delay : this._scatterTVSpine.skeleton.data.findAnimation(TV_ANIMATION_NAMES.x_split_right).duration}));
        }
        this._splitTL.add(() => {
            this._scatterTVSpine.skeleton.setSkinByName(TV_SKIN_NAMES.wild);
            SpineUtils.resetSpineAnimation(this._scatterTVSpine);
            this._scatterTVSpine.state.setEmptyAnimation(SCATTER_ANIMATION_TRACKS.xSplit, 0);
            this._scatterTVSpine.state.setAnimation(SCATTER_ANIMATION_TRACKS.idleLoop, TV_ANIMATION_NAMES.idle_loop, true);
        });
        this._splitTL.add(() => {
            Loner.soundPlayer.playSound(LonerSound.TV_WILD);
        }, 0.49);
        return this._splitTL;
    }

    public getInfectiousTL(symbolName:string):gsap.core.Timeline {
        this._tvInfectedOrSplit = true;
        if(this._tvRevealTL && this._tvRevealTL.isActive()) {
            this._tvRevealTL.totalProgress(1);
        }
        if(this._tvLandTL && this._tvLandTL.isActive()) {
            this._tvLandTL.totalProgress(1);
        }
        this._splitLevel = Loner.model.symbolSizes[this.reelId][this.reelPosition - 3];
        this._infectiousTL = gsap.timeline();
        this._infectiousTL.add(() => {
            this.setSpinePosition();
        });
        this._infectiousTL.add(() => {
            Loner.soundPlayer.playSound(LonerSound.TV_INFECT);
            this._scatterTVSpine.visible = true;
            SpineUtils.resetSpineAnimation(this._scatterTVSpine);
            this.isInfected = true;
            let splitLevelString:string = "";
            if(this.splitLevel > 1) {
                splitLevelString = "_Split_" + this.splitLevel;
            }
            this.featureTextureName = symbolName + "/" + symbolName + splitLevelString + "_infectious";
            this.getNewStateAnimation(LonerSymbolStates.SCATTER_INFECTED);
        });
        this._infectiousTL.add(() => {
            this._scatterTVInfectSpine.visible = true;
            this._scatterTVInfectSpine.skeleton.setSkinByName((symbolName.toLowerCase() + "_" + this._splitLevel.toString()));
        });
        this._infectiousTL.add(() => {
            SpineUtils.resetSpineAnimation(this._scatterTVInfectSpine);
            this._scatterTVSpine.state.setEmptyAnimation(SCATTER_ANIMATION_TRACKS.idleLoop, 0);
            this._scatterTVSpine.state.setEmptyAnimation(SCATTER_ANIMATION_TRACKS.tvTransformed, 0);
            this._scatterTVSpine.state.setEmptyAnimation(SCATTER_ANIMATION_TRACKS.tvLand, 0);
            this._scatterTVInfectSpine.state.setAnimation(SCATTER_ANIMATION_TRACKS.infectious, TV_INFECTIOUS_ANIMATION_NAMES.infect, false)
        });
        this._infectiousTL.add(gsap.timeline({delay : this._scatterTVInfectSpine.skeleton.data.findAnimation(TV_INFECTIOUS_ANIMATION_NAMES.infect).duration}));

        this._infectiousTL.add(() => {
            this._scatterTVSpine.state.setEmptyAnimation(SCATTER_ANIMATION_TRACKS.infectious, 0);
            this._scatterTVInfectSpine.state.setAnimation(SCATTER_ANIMATION_TRACKS.idleLoop, TV_INFECTIOUS_ANIMATION_NAMES.idle_loop, true);
        });

        return this._infectiousTL;
    }

    public getTVLandTL():gsap.core.Timeline {
        this._tvLandTL = gsap.timeline();

        if (this._tvInfectedOrSplit == true || this._tvLanded == true || this._tvTransformed == true){
            return this._tvLandTL;
        }
        this._tvLanded = true;
        this._tvLandTL.add(() => {
            this.setSpinePosition();
        });
        this._tvLandTL.add(() => {
            this._scatterTVSpine.visible = true;
            SpineUtils.resetSpineAnimation(this._scatterTVSpine);
        });

        this._tvLandTL.add(() => {
            this._scatterTVSpine.state.setAnimation(SCATTER_ANIMATION_TRACKS.tvLand, TV_ANIMATION_NAMES.land, false)
        });
        this._tvLandTL.add(gsap.timeline({delay : this._scatterTVSpine.skeleton.data.findAnimation(TV_ANIMATION_NAMES.land).duration}));

        this._tvLandTL.add(() => {
            this._scatterTVSpine.state.setEmptyAnimation(SCATTER_ANIMATION_TRACKS.tvLand, 0);
            this._scatterTVSpine.state.setAnimation(SCATTER_ANIMATION_TRACKS.idleLoop, TV_ANIMATION_NAMES.static, true);
        });

        return this._tvLandTL;
    }

    public getTVTransformTL():gsap.core.Timeline {
        this._tvRevealTL = gsap.timeline();

        if (this._tvInfectedOrSplit == true || this._tvTransformed == true){
            return this._tvRevealTL;
        }
        this._tvTransformed = true;

        let newAnimationName:string = "";
        let newSkinName:string = "";
        this.transformedState = Loner.model.scatterTransform[this.reelId];
        if(this._tvLandTL && this._tvLandTL.isActive()) {
            this._tvLandTL.totalProgress(1);
            this._tvLandTL.kill();
        }

        switch(this.transformedState) {
            case LonerSymbolStates.SCATTER_SHOOT:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.shoot;
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.shoot;
                break;
            case LonerSymbolStates.SCATTER_SHOOT_SHOOT:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.shoot;
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.shootShoot;
                break;
            case LonerSymbolStates.SCATTER_DUCK:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.duck;
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.duck;
                break;
            case LonerSymbolStates.SCATTER_PIN:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.pin;
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.pin;
                break;
            case LonerSymbolStates.SCATTER_THE:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.the;
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.the;
                break;
            case LonerSymbolStates.SCATTER_WIN:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.win;
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.win;
                break;
            case LonerSymbolStates.SCATTER_TICK:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.tick_1;
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.tick;
                break;
            case LonerSymbolStates.SCATTER_TICK_TICK:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.tick_2;
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.tickTick;
                break;
            case LonerSymbolStates.SCATTER_BOOM:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.boom;
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.boom;
                break;
            case LonerSymbolStates.SCATTER_WILD:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.wild;
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.wild;
                break;
            case LonerSymbolStates.SCATTER_XSPLIT:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.x_split;
                //Will end up as Wild
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.wild;
                break;
            case LonerSymbolStates.SCATTER_XWAYS:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.infectious;
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.infectious;
                break;
            case LonerSymbolStates.M:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.M;
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.M;
                break;
            case LonerSymbolStates.A:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.A;
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.A;
                break;
            case LonerSymbolStates.X:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.X;
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.X;
                break;
            case LonerSymbolStates.SAD:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.sad;
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.sad;
                break;
            case LonerSymbolStates.SCATTER_MAX_WIN:
                newAnimationName = TV_ANIMATION_NAMES.reveal;
                newSkinName = TV_SKIN_NAMES.max_win;
                this.featureTextureName = SCATTER_TRANSFORM_TEXTURES.max_win;
                break;
            default:
        }

        this._tvRevealTL.add(() => {
            if(Loner.model.data.nextMode == LonerGameMode.NORMAL) {
                if(newSkinName === TV_SKIN_NAMES.max_win) {
                    Loner.soundPlayer.fadeMusic(1, 0, 0.1);
                    Loner.soundPlayer.playSound(LonerSound.MAIN_GAME_MAX_WIN_SYM_LAND);
                }
                if(!Loner.model.instantTVReveal[this.reelId] == true) {
                    if(this.reelId == 0) {
                        Loner.soundPlayer.stopSound(LonerSound.SCATTER_1_LAND);
                    } else if(this.reelId == 2) {
                        Loner.soundPlayer.stopSound(LonerSound.SCATTER_2_LAND);
                    } else if(this.reelId == 4) {
                        Loner.soundPlayer.stopSound(LonerSound.SCATTER_3_LAND);
                    }
                }
                if(Loner.apiPlugin.settings.get(APISetting.FAST_SPIN, false)) {
                    if(this.reelId === lastReelOfScatter()) {
                        Loner.soundPlayer.playSound(LonerSound.TV_LAND);
                    }
                } else {
                    Loner.soundPlayer.playSound(LonerSound.TV_LAND);
                }
                if(newSkinName == TV_SKIN_NAMES.wild) {
                    if(Loner.apiPlugin.settings.get(APISetting.FAST_SPIN) && this.reelId === 4) {
                        Loner.soundPlayer.playSound(LonerSound.TV_WILD);
                    } else {
                        let noOfWild:number = 0;
                        if(Loner.model.gameData.featureInfo && Loner.model.gameData.featureInfo.ScatterReveal) {
                            for(let idx:number = 0; idx < Loner.model.gameData.featureInfo.ScatterReveal.length; idx++) {
                                if(Loner.model.gameData.featureInfo.ScatterReveal[idx].feature === "WILD") {
                                    noOfWild++;
                                }
                            }
                        }
                        if(noOfWild > 1) {
                            if(this.reelId === lastReelOfScatter("WILD")) {
                                Loner.soundPlayer.playSound(LonerSound.TV_WILD);
                            }
                        } else {
                            Loner.soundPlayer.playSound(LonerSound.TV_WILD);
                        }
                    }
                }
            }

            function lastReelOfScatter(specificScatter:string | undefined = undefined):number {
                let lastReelOfWild:number = -1;
                if(Loner.model.gameData.featureInfo && Loner.model.gameData.featureInfo.ScatterReveal) {
                    for(let idx:number = 0; idx < Loner.model.gameData.featureInfo.ScatterReveal.length; idx++) {
                        if(specificScatter && Loner.model.gameData.featureInfo.ScatterReveal[idx].position[0] > lastReelOfWild && Loner.model.gameData.featureInfo.ScatterReveal[idx].feature === specificScatter) {
                            lastReelOfWild = Loner.model.gameData.featureInfo.ScatterReveal[idx].position[0];
                        } else {
                            if(Loner.model.gameData.featureInfo.ScatterReveal[idx].position[0] > lastReelOfWild) {
                                lastReelOfWild = Loner.model.gameData.featureInfo.ScatterReveal[idx].position[0];
                            }
                        }
                    }
                }
                return lastReelOfWild;
            }
        });
        this._tvRevealTL.add(() => {
            this.setSpinePosition();
        });
        this._tvRevealTL.add(() => {
            this._scatterTVSpine.visible = true;
            this._scatterTVSpine.skeleton.setSkinByName(newSkinName);
        });
        this._tvRevealTL.add(() => {
            SpineUtils.resetSpineAnimation(this._scatterTVSpine);
            this._scatterTVSpine.state.setEmptyAnimation(SCATTER_ANIMATION_TRACKS.tvLand, 0);
            this._scatterTVSpine.state.setEmptyAnimation(SCATTER_ANIMATION_TRACKS.idleLoop, 0);
            this._scatterTVSpine.state.setAnimation(SCATTER_ANIMATION_TRACKS.tvTransformed, newAnimationName, false)
        });
        this._tvRevealTL.add(gsap.timeline({delay : this._scatterTVSpine.skeleton.data.findAnimation(newAnimationName).duration}));

        this._tvRevealTL.add(() => {
            if(Loner.model.data.nextMode !== LonerGameMode.NORMAL) {
                if(this._reelId == 4) {
                    Loner.soundPlayer.playSound(this.getFeatureActivationSound());
                }
            }
        });
        this._tvRevealTL.add(() => {
            this._scatterTVSpine.state.setEmptyAnimation(SCATTER_ANIMATION_TRACKS.tvTransformed, 0);
            this._scatterTVSpine.state.setAnimation(SCATTER_ANIMATION_TRACKS.idleLoop, TV_ANIMATION_NAMES.idle_loop, true);
        });
        this._tvRevealTL.add(() => {
            if(this.isInfected == false) {
                this.getNewStateAnimation(LonerSymbolStates.SCATTER_TRANSFORMED);
            }
        });
        if(newSkinName === TV_SKIN_NAMES.max_win) {
            this._tvRevealTL.to(this, {duration : 2});
        }

        return this._tvRevealTL;
    }

    public startScatterMaxWinAnimation():void {
        this.setSpinePosition();
        this._scatterTVSpine.state.setAnimation(SCATTER_ANIMATION_TRACKS.maxWin, TV_ANIMATION_NAMES.maxwin, true);
    }

    public reset():void {
        this._tvLanded = false;
        this._tvTransformed = false;
        this._tvInfectedOrSplit = false;
        this._reelId = -1;
        this.symbolName = "S";
        super.reset();
    }

    private getFeatureActivationSound():string {
        Loner.soundPlayer.fadeMusic(1, 0, 0.1);
        Loner.soundPlayer.stopSound(LonerSound.START_GAME);
        switch(Loner.model.data.nextMode) {
            case LonerGameMode.FEATURE_PIN_THE_WIN :
                return LonerSound.PTW_ACTIVATION;
            case LonerGameMode.FEATURE_TICK_TICK_BOOM :
                return LonerSound.TTB_ACTIVATION;
            case LonerGameMode.FEATURE_SHOOT_SHOOT_DUCK :
                return LonerSound.SSD_ACTIVATION;
            default :
                return "";
        }
    }
}